import { AnyAction, ThunkAction } from '@reduxjs/toolkit';

import axios from 'old-store/utils/axios';
import { RootState } from 'old-store/store';
import { setSharingUrl, startSharingLoad, stopSharingLoad } from 'store/slices/sharing';
import {
  checkIfAuthorizedAndLogoutIfNot,
  fetchCustomerToken
} from 'store/slices/selection/actions';
import { getTranslationKey } from 'helpers/texting';
import { getErrorStatus } from 'helpers/network';
import { toast } from 'react-toastify';
import ApiErrors from 'old-store/utils/API/APIErrors';
import { toUnicode } from 'helpers/punycodeHelpers';

interface ISetArguments {
  url: string;
  type: string;
  identifier: string;
}

interface IGetArguments {
  type: string;
  identifier?: string;
  onlyPublicImages?: boolean;
  refetch?: boolean;
  callback?: (linkUrl: string, type: string, identifier: string) => void;
  showToast?: boolean;
}

const showLinkCopiedToast = () =>
  toast.success(getTranslationKey('shareView.linkCopied'), {
    theme: 'colored',
    hideProgressBar: true,
    autoClose: 4000
  });

export const setShareLink =
  ({ url, type, identifier }: ISetArguments): ThunkAction<void, RootState, unknown, AnyAction> =>
  (dispatch) => {
    try {
      dispatch(setSharingUrl({ type, identifier, url: toUnicode(encodeURIComponent(url)) }));
    } catch (e) {
      console.error(e);
    }
  };

export const getShareLink =
  ({
    type,
    identifier = '',
    onlyPublicImages,
    refetch,
    callback,
    showToast
  }: IGetArguments): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const {
        sharing,
        collection,
        image: { curGalleryImage }
      } = getState();

      dispatch(startSharingLoad());

      const accessIdentifier = identifier === 'curGalleryImage' ? curGalleryImage._id : identifier;
      const accessKey = `${type}${accessIdentifier || ''}Url`;

      if (!refetch && sharing.storedUrls[accessKey]) {
        dispatch(
          setSharingUrl({
            type,
            identifier: accessIdentifier,
            url: toUnicode(sharing.storedUrls[accessKey])
          })
        );

        if (callback) callback(toUnicode(sharing.storedUrls[accessKey]), type, accessIdentifier);
        if (showToast) {
          showLinkCopiedToast();
        }

        return;
      }

      await fetchCustomerToken(collection._user);

      const res = await axios.get('/api/collection/share', {
        params: {
          type,
          identifier: accessIdentifier,
          onlyPublicImages,
          collection_id: window.SITE_ID
        }
      });
      ApiErrors.checkOnApiError(res);

      if (res.data.id) {
        const linkStr = `${window.location.protocol}//${window.location.host}/share/${res.data.id}`;
        const linkUrl = toUnicode(encodeURIComponent(linkStr));

        dispatch(setSharingUrl({ type, identifier: accessIdentifier, url: linkUrl }));

        if (callback) callback(linkUrl, type, accessIdentifier);
        if (showToast) {
          showLinkCopiedToast();
        }
      }
    } catch (e: any) {
      const { collection } = getState();
      checkIfAuthorizedAndLogoutIfNot(getErrorStatus(e) ?? 0, collection._user);

      console.log(e);
    } finally {
      dispatch(stopSharingLoad());
    }
  };
